import { get, set, del, keys } from 'idb-keyval';

export type PersistenceKey =
  | 'transactions'
  | 'categories'
  | 'budgets'
  | 'corrections';

const STORAGE_KEYS: PersistenceKey[] = [
  'transactions',
  'categories',
  'budgets',
  'corrections',
];

export interface PersistenceData<T> {
  data: T;
  timestamp: number;
}

let isIndexedDBAvailable: boolean | null = null;

async function checkIndexedDBAvailability(): Promise<boolean> {
  if (isIndexedDBAvailable !== null) {
    return isIndexedDBAvailable;
  }

  try {
    const testKey = '__idb_test__';
    await set(testKey, 'test');
    await del(testKey);
    isIndexedDBAvailable = true;
  } catch {
    isIndexedDBAvailable = false;
  }

  return isIndexedDBAvailable;
}

export async function saveData<T>(
  key: PersistenceKey,
  data: T
): Promise<boolean> {
  const isAvailable = await checkIndexedDBAvailability();
  if (!isAvailable) {
    console.warn('IndexedDB is not available, data will not persist');
    return false;
  }

  try {
    const payload: PersistenceData<T> = {
      data,
      timestamp: Date.now(),
    };
    await set(key, payload);
    return true;
  } catch (error) {
    console.error(`Failed to save data for key "${key}":`, error);
    return false;
  }
}

export async function loadData<T>(
  key: PersistenceKey
): Promise<PersistenceData<T> | null> {
  const isAvailable = await checkIndexedDBAvailability();
  if (!isAvailable) {
    return null;
  }

  try {
    const result = await get<PersistenceData<T>>(key);
    return result ?? null;
  } catch (error) {
    console.error(`Failed to load data for key "${key}":`, error);
    return null;
  }
}

export async function deleteData(key: PersistenceKey): Promise<boolean> {
  const isAvailable = await checkIndexedDBAvailability();
  if (!isAvailable) {
    return false;
  }

  try {
    await del(key);
    return true;
  } catch (error) {
    console.error(`Failed to delete data for key "${key}":`, error);
    return false;
  }
}

export async function clearAllData(): Promise<boolean> {
  const isAvailable = await checkIndexedDBAvailability();
  if (!isAvailable) {
    return false;
  }

  try {
    await Promise.all(STORAGE_KEYS.map((key) => del(key)));
    return true;
  } catch (error) {
    console.error('Failed to clear all data:', error);
    return false;
  }
}

export async function getAllStoredKeys(): Promise<PersistenceKey[]> {
  const isAvailable = await checkIndexedDBAvailability();
  if (!isAvailable) {
    return [];
  }

  try {
    const allKeys = await keys<PersistenceKey>();
    return allKeys.filter((key) =>
      STORAGE_KEYS.includes(key as PersistenceKey)
    );
  } catch (error) {
    console.error('Failed to get stored keys:', error);
    return [];
  }
}

export async function loadAllData(): Promise<
  Partial<Record<PersistenceKey, PersistenceData<unknown>>>
> {
  const isAvailable = await checkIndexedDBAvailability();
  if (!isAvailable) {
    return {};
  }

  try {
    const results = await Promise.all(
      STORAGE_KEYS.map(async (key) => {
        const data = await loadData(key);
        return [key, data] as const;
      })
    );

    return Object.fromEntries(results.filter(([, data]) => data !== null));
  } catch (error) {
    console.error('Failed to load all data:', error);
    return {};
  }
}

export function isAvailable(): Promise<boolean> {
  return checkIndexedDBAvailability();
}

export function resetAvailabilityCheck(): void {
  isIndexedDBAvailable = null;
}
